// Lista własnych ofert SalesCRM dla zalogowanego klienta.
//
// Wymieniona zawartość poprzedniej strony (która listowała produkty dedykowane
// z modułu Cms/Offer) - teraz pokazuje oferty handlowca przesłane do akceptacji
// oraz historię (zaakceptowane / odrzucone / wygasłe / anulowane).

import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import classnames from 'classnames';
import { CircularProgress } from '@mui/material';

import { reduxActions, useDispatch } from 'store';
import { useGetSalesCrmMyOffers } from 'api';
import { OfferStatusBadge, PageTitle, Pagination, SearchInput } from 'components/controls';

import styles from 'theme/pages/OffersDedicated/OffersDedicated.module.scss';

const formatDate = (raw: string | null): string => {
  if (!raw) return '-';
  // Backend zwraca format "YYYY-MM-DD HH:mm:ss" (bez T) lub ISO - bierzemy pierwsze 10 znaków YYYY-MM-DD.
  const isoDate = raw.length >= 10 ? raw.substring(0, 10) : raw;
  const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw;
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
  }
  return `${match[3]}.${match[2]}.${match[1]}`;
};

const OffersDedicated: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [queryParams, setQueryParams] = useState({
    page: 1,
    limit: 20,
    search_keyword: '',
    is_periodic: false
  });

  const { data, isLoading, isError } = useGetSalesCrmMyOffers(queryParams);

  useEffect(() => {
    dispatch(
      reduxActions.setBreadcrumbs([
        { name: t('Dashboard'), path: '/dashboard' },
        { name: t('Oferty dedykowane') }
      ])
    );
  }, []);

  const items = data?.items ?? [];
  const totalPages = data?.total_pages ?? 1;
  const totalCount = data?.total_count ?? 0;

  return (
    <div className={classnames(styles.componentWrapper, 'StylePath-Pages-OffersDedicated')}>
      <PageTitle
        title={
          <>
            <Trans>Oferty dedykowane</Trans>{' '}
            <span className={styles.thin}>({totalCount})</span>
          </>
        }
      />

      <div className={styles.header}>
        <div className={styles.searchFormWrapper}>
          <SearchInput
            placeholder={`${t('Szukaj po numerze oferty')}...`}
            onChange={(search_keyword) =>
              setQueryParams((prev) => ({ ...prev, page: 1, search_keyword }))
            }
          />
        </div>
      </div>

      {isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <CircularProgress size={28} />
        </div>
      )}

      {!isLoading && isError && (
        <div style={{ padding: 24, color: '#b3142d' }}>
          {t('Nie udało się pobrać ofert. Spróbuj ponownie później.')}
        </div>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <div style={{ padding: 24, color: '#4b556a' }}>
          {t('Brak ofert do wyświetlenia.')}
        </div>
      )}

      {!isLoading && !isError && items.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ backgroundColor: '#f5f6fa', textAlign: 'left' }}>
                <th style={{ padding: '12px 16px' }}>{t('Numer oferty')}</th>
                <th style={{ padding: '12px 16px' }}>{t('Status')}</th>
                <th style={{ padding: '12px 16px' }}>{t('Data utworzenia')}</th>
                <th style={{ padding: '12px 16px', textAlign: 'right' }}>{t('Akcja')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((offer) => (
                <tr key={offer.id} style={{ borderBottom: '1px solid #eef0f4' }}>
                  <td style={{ padding: '12px 16px', fontWeight: 600 }}>
                    {offer.number ?? `#${offer.id}`}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <OfferStatusBadge status={offer.status} label={offer.status_formatted} />
                  </td>
                  <td style={{ padding: '12px 16px', color: '#4b556a' }}>
                    {formatDate(offer.sys_insert_date)}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    <button
                      type="button"
                      onClick={() => navigate(`/dashboard/offers/dedicated/${offer.id}`)}
                      style={{
                        padding: '6px 16px',
                        borderRadius: 6,
                        border: '1px solid #6b54ff',
                        background: '#fff',
                        color: '#6b54ff',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}>
                      {t('Zobacz')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        pagesCount={totalPages}
        page={queryParams.page}
        onChange={(page) => setQueryParams((prev) => ({ ...prev, page }))}
      />
    </div>
  );
};

export default OffersDedicated;
